export type BKTParams = {
  topicId: string;
  pL0: number;
  pT: number;
  pS: number;
  pG: number;
  pKnown: number;
  attempts: number;
  lastUpdated: string;
};

export type BKTUpdateResult = {
  topicId: string;
  previousProbability: number;
  newProbability: number;
  evidenceWeight: number;
  updatedParams: BKTParams;
};

export type StudentMasteryState = {
  studentId: string;
  topicMastery: Record<string, BKTParams>;
  lastUpdated: string;
};

const DEFAULT_BKT_PARAMS: Omit<BKTParams, "topicId"> = {
  pL0: 0.3,
  pT: 0.1,
  pS: 0.1,
  pG: 0.25,
  pKnown: 0.3,
  attempts: 0,
  lastUpdated: new Date().toISOString()
};

export function initializeBKTForTopic(topicId: string, initialProbability?: number): BKTParams {
  const initial = initialProbability ?? 0.3;

  return {
    topicId,
    pL0: initial,
    pT: 0.1,
    pS: 0.1,
    pG: 0.25,
    pKnown: initial,
    attempts: 0,
    lastUpdated: new Date().toISOString()
  };
}

export function updateBKTMastery(
  params: BKTParams,
  correct: boolean
): BKTUpdateResult {
  const { pT, pS, pG, pKnown } = params;
  
  let pL = pKnown;
  
  if (correct) {
    const numerator = pL * (1 - pS);
    const denominator = numerator + (1 - pL) * pG;
    pL = denominator === 0 ? 0.5 : numerator / denominator;
    
    pL = Math.min(0.99, Math.max(0.01, pL + pT * (1 - pL)));
  } else {
    const numerator = pL * pS;
    const denominator = numerator + (1 - pL) * (1 - pG);
    pL = denominator === 0 ? 0.5 : numerator / denominator;
    
    pL = Math.max(0.01, Math.min(0.99, pL - 0.05 * pL));
  }
  
  const evidenceWeight = Math.abs(pL - pKnown);
  
  const updatedParams: BKTParams = {
    ...params,
    pKnown: pL,
    attempts: params.attempts + 1,
    lastUpdated: new Date().toISOString()
  };
  
  return {
    topicId: params.topicId,
    previousProbability: params.pKnown,
    newProbability: pL,
    evidenceWeight,
    updatedParams
  };
}

export function updateBKTMasteryFromGapSignal(
  params: BKTParams,
  severity: "low" | "medium" | "high",
  confidence: number
): BKTUpdateResult {
  let probabilityChange = 0;
  
  switch (severity) {
    case "high":
      probabilityChange = -0.15 * confidence;
      break;
    case "medium":
      probabilityChange = -0.08 * confidence;
      break;
    case "low":
      probabilityChange = -0.03 * confidence;
      break;
  }
  
  const newProbability = Math.max(0.01, Math.min(0.99, params.pKnown + probabilityChange));
  const evidenceWeight = Math.abs(newProbability - params.pKnown);
  
  const updatedParams: BKTParams = {
    ...params,
    pKnown: newProbability,
    attempts: params.attempts + 1,
    lastUpdated: new Date().toISOString()
  };
  
  return {
    topicId: params.topicId,
    previousProbability: params.pKnown,
    newProbability,
    evidenceWeight,
    updatedParams
  };
}

export function calculateMasteryPercent(pKnown: number): number {
  return Math.round(pKnown * 100);
}

export function getMasteryLevel(percent: number): "needs_work" | "developing" | "competent" | "proficient" {
  if (percent < 40) return "needs_work";
  if (percent < 60) return "developing";
  if (percent < 80) return "competent";
  return "proficient";
}

export function getMasteryColor(percent: number): string {
  if (percent < 40) return "#d96b6b";
  if (percent < 60) return "#ff5a36";
  if (percent < 80) return "#ff5a36";
  return "#52c41a";
}

export function getMasteryLabel(percent: number): string {
  if (percent < 40) return "Needs Work";
  if (percent < 60) return "Developing";
  if (percent < 80) return "Competent";
  return "Proficient";
}

export function initializeStudentMasteryState(
  studentId: string,
  topicIds: string[]
): StudentMasteryState {
  const topicMastery: Record<string, BKTParams> = {};
  
  for (const topicId of topicIds) {
    topicMastery[topicId] = initializeBKTForTopic(topicId);
  }
  
  return {
    studentId,
    topicMastery,
    lastUpdated: new Date().toISOString()
  };
}

export function getTopicMastery(
  state: StudentMasteryState,
  topicId: string
): BKTParams | null {
  return state.topicMastery[topicId] || null;
}

export function updateStudentMastery(
  state: StudentMasteryState,
  topicId: string,
  update: BKTUpdateResult
): StudentMasteryState {
  return {
    studentId: state.studentId,
    topicMastery: {
      ...state.topicMastery,
      [topicId]: update.updatedParams
    },
    lastUpdated: new Date().toISOString()
  };
}

export function calculateAverageMastery(state: StudentMasteryState): number {
  const masteries = Object.values(state.topicMastery);
  if (masteries.length === 0) return 0;
  
  const sum = masteries.reduce((acc, m) => acc + m.pKnown, 0);
  return Math.round((sum / masteries.length) * 100);
}

export function getWeakestTopics(
  state: StudentMasteryState,
  limit: number = 5
): Array<{ topicId: string; mastery: BKTParams }> {
  return Object.entries(state.topicMastery)
    .map(([topicId, mastery]) => ({ topicId, mastery }))
    .sort((a, b) => a.mastery.pKnown - b.mastery.pKnown)
    .slice(0, limit);
}

export function getStrongestTopics(
  state: StudentMasteryState,
  limit: number = 5
): Array<{ topicId: string; mastery: BKTParams }> {
  return Object.entries(state.topicMastery)
    .map(([topicId, mastery]) => ({ topicId, mastery }))
    .sort((a, b) => b.mastery.pKnown - a.mastery.pKnown)
    .slice(0, limit);
}

export function getTopicsNeedingReview(
  state: StudentMasteryState,
  threshold: number = 0.5
): Array<{ topicId: string; mastery: BKTParams }> {
  return Object.entries(state.topicMastery)
    .map(([topicId, mastery]) => ({ topicId, mastery }))
    .filter(({ mastery }) => mastery.pKnown < threshold)
    .sort((a, b) => a.mastery.pKnown - b.mastery.pKnown);
}

export function serializeMasteryState(state: StudentMasteryState): string {
  return JSON.stringify(state);
}

export function deserializeMasteryState(json: string): StudentMasteryState | null {
  try {
    const parsed = JSON.parse(json);
    if (parsed.studentId && parsed.topicMastery) {
      return parsed as StudentMasteryState;
    }
    return null;
  } catch {
    return null;
  }
}

export function mergeGapSignalsIntoMastery(
  state: StudentMasteryState,
  gapSignals: Array<{
    topicId: string;
    severity: "low" | "medium" | "high";
    confidence?: number;
  }>
): StudentMasteryState {
  let newState = { ...state };
  
  for (const signal of gapSignals) {
    const currentMastery = newState.topicMastery[signal.topicId];
    if (currentMastery) {
      const update = updateBKTMasteryFromGapSignal(
        currentMastery,
        signal.severity,
        signal.confidence || 0.7
      );
      newState = updateStudentMastery(newState, signal.topicId, update);
    }
  }
  
  return newState;
}

export function calculateMasteryTrend(
  history: Array<{ timestamp: string; mastery: number }>
): "improving" | "stable" | "declining" {
  if (history.length < 3) return "stable";
  
  const recent = history.slice(-3).map(h => h.mastery);
  const older = history.slice(-6, -3).map(h => h.mastery);
  
  if (older.length === 0) return "stable";
  
  const recentAvg = recent.reduce((a, b) => a + b, 0) / recent.length;
  const olderAvg = older.reduce((a, b) => a + b, 0) / older.length;
  
  const diff = recentAvg - olderAvg;
  
  if (diff > 0.05) return "improving";
  if (diff < -0.05) return "declining";
  return "stable";
}
